import { useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase as defaultSupabase } from '@/lib/supabase';

export type RealtimeConnectionStatus = 'connecting' | 'connected' | 'error' | 'disabled';

export interface RealtimeConfig {
  table: string;
  schema?: string;
  filter?: string;
  /** false desliga a assinatura (status 'disabled') — pra canais que só fazem
   *  sentido com a modal/feature aberta, sem violar regras de hooks. */
  enabled?: boolean;
}

/** Shape mínimo do payload de postgres_changes repassado ao onEvent (aditivo —
 *  callbacks antigos sem parâmetro continuam válidos). `new`/`old` são as rows. */
export interface RealtimeEventPayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new?: Record<string, unknown> | null;
  old?: Record<string, unknown> | null;
}

/**
 * Generic hook that subscribes to a single Supabase Realtime postgres_changes
 * channel. Calls `onEvent` on every INSERT / UPDATE / DELETE. Unsubscribes
 * automatically when the component unmounts.
 *
 * Ports-only: accepts a `client` param (default = the shared supabase client
 * from @/lib/supabase) so tests can inject a mock without importing
 * @supabase/supabase-js directly.
 */
export function useSupabaseRealtime(
  config: RealtimeConfig,
  onEvent: (payload?: RealtimeEventPayload) => void,
  client: SupabaseClient = defaultSupabase,
): RealtimeConnectionStatus {
  const [status, setStatus] = useState<RealtimeConnectionStatus>('connecting');

  // Keep a stable ref so the effect closure never goes stale
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const { table, schema = 'public', filter, enabled = true } = config;

  useEffect(() => {
    if (!enabled) {
      setStatus('disabled');
      return;
    }
    const channelName = `realtime-dashboard:${schema}:${table}${filter ? `:${filter}` : ''}`;

    // Build the subscription. channel.on() returns `this` so it is chainable.
    const channel = client
      .channel(channelName)
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'postgres_changes' as any,
        {
          event: '*' as const,
          schema,
          table,
          ...(filter !== undefined ? { filter } : {}),
        },
        (payload: Record<string, unknown>) => {
          // Repassa a row do evento — permite ao caller fazer patch local em
          // vez de refetch (corta consultas desnecessárias; pedido Breno 11/06).
          onEventRef.current(payload as unknown as RealtimeEventPayload);
        },
      )
      .subscribe((channelStatus: string) => {
        if (channelStatus === 'SUBSCRIBED') {
          setStatus('connected');
        } else if (channelStatus === 'CHANNEL_ERROR' || channelStatus === 'TIMED_OUT') {
          setStatus('error');
        } else if (channelStatus === 'CLOSED') {
          setStatus('disabled');
        }
      });

    return () => {
      // Graceful cleanup — ignore errors (e.g. already closed)
      void client.removeChannel(channel);
    };
  // Primitive deps only to avoid re-subscribing on every render when caller
  // passes an inline config object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, table, schema ?? 'public', filter ?? '', enabled]);

  return status;
}
